import { useCallback } from 'react';
import { AUTOMATIC_PRESENTATIONS, useAutomaticPresentation } from '#shared/lib/automaticPresentation';
import { useNewsSeenUnreadCount } from '#shared/lib/dashboardNews';

/**
 * Связывает условия и сохранённое состояние News с общей очередью автоматических показов.
 * Счётчик непрочитанных остаётся продуктовой ответственностью News, а coordinator решает,
 * когда подсказка может занять единственный слот приложения.
 *
 * @kind helper
 * @param unreadCount Счётчик от backend; `undefined` означает, что результат запроса ещё не получен.
 * @param canShowTooltip Разрешает подсказку на текущей странице, например запрещает её внутри раздела News.
 * @returns Состояние подсказки и News-индикатора, а также действия, завершающие автоматический показ.
 */
export function useDashboardNewsPresentation(unreadCount: number | undefined, canShowTooltip: boolean) {
    const { tooltipEligibility, hasUnreadMessages, markCurrentAsSeen } = useNewsSeenUnreadCount(
        unreadCount,
        canShowTooltip
    );
    const { isActive, startLeaving, completeLeaving } = useAutomaticPresentation({
        ...AUTOMATIC_PRESENTATIONS.LOW.dashboardNews,
        eligibility: tooltipEligibility,
    });

    const finishPresentation = useCallback(
        (reason: 'dismissed' | 'completed') => {
            const wasActive = startLeaving(reason);

            // Действие пользователя сохраняет текущий счётчик и тогда, когда подсказка ещё ждёт своей очереди.
            markCurrentAsSeen();

            // Desktop Tooltip удаляет закрытый popup сразу и не имеет отдельного lifecycle завершения,
            // поэтому после пользовательского действия слот можно освободить без дополнительного таймера.
            if (wasActive) {
                completeLeaving();
            }
        },
        [completeLeaving, markCurrentAsSeen, startLeaving]
    );
    const dismissTooltip = useCallback(() => finishPresentation('dismissed'), [finishPresentation]);
    const markCurrentNewsAsSeen = useCallback(() => finishPresentation('completed'), [finishPresentation]);

    return {
        isTooltipVisible: isActive,
        hasUnreadMessages,
        dismissTooltip,
        markCurrentNewsAsSeen,
    };
}
